// Markdown de la base de conocimiento (Manual): convierte el texto de un articulo en bloques para dibujarlo con React.
// No produce HTML: la pantalla arma cada bloque con elementos de React y solo deja pasar enlaces seguros, asi que un
// articulo no puede meter codigo en la pagina. Funciones puras.
// Ademas del Markdown comun (titulos, listas, tablas, codigo, citas, enlaces e imagenes) entiende:
//   - listas numeradas como pasos: "1. **Titulo del paso** resto" (o la primera linea como titulo si el paso tiene mas);
//   - avisos: "> **Ojo:** …", "> **Consejo:** …", "> **Importante:** …";
//   - adjuntos recuperados de Zammad: ![nombre](kb-adjunto:<id>);
//   - accesos de la boveda: [🔒 Acceso: Titulo](acceso:<id>).

export type EnLinea =
  | { t: 'texto'; v: string }
  | { t: 'negrita'; hijos: EnLinea[] }
  | { t: 'cursiva'; hijos: EnLinea[] }
  | { t: 'codigo'; v: string }
  | { t: 'enlace'; href: string; hijos: EnLinea[] }
  | { t: 'imagen'; src: string; alt: string }
  | { t: 'adjunto'; id: string; nombre: string }
  | { t: 'acceso'; id: string; titulo: string }
  | { t: 'salto' };

export type TipoAviso = 'ojo' | 'consejo' | 'importante';
export type Alineacion = 'izq' | 'centro' | 'der' | null;

export type Bloque =
  | { t: 'titulo'; nivel: number; id: string; hijos: EnLinea[]; texto: string }
  | { t: 'parrafo'; hijos: EnLinea[] }
  | { t: 'lista'; items: Bloque[][] }
  | { t: 'pasos'; inicio: number; items: { titulo: EnLinea[] | null; cuerpo: Bloque[] }[] }
  | { t: 'codigo'; lenguaje: string; texto: string }
  | { t: 'tabla'; encabezado: EnLinea[][]; alineacion: Alineacion[]; filas: EnLinea[][][] }
  | { t: 'aviso'; tipo: TipoAviso; cuerpo: Bloque[] }
  | { t: 'cita'; cuerpo: Bloque[] }
  | { t: 'regla' };

// Texto que dejo la migracion cuando el articulo de Zammad solo tenia adjuntos.
export const SOLO_ADJUNTOS = /Este art[ií]culo en Zammad solo ten[ií]a adjuntos/i;

// ---------- Enlaces seguros ----------

// Solo http(s), correo, telefono y rutas de la propia Consola. Todo lo demas (javascript:, data:, vbscript:...) se descarta.
export function enlaceSeguro(href: string): string | null {
  const h = href.trim();
  if (/^https?:\/\//i.test(h) || /^mailto:/i.test(h) || /^tel:/i.test(h)) return h;
  if (/^\/(?!\/)/.test(h) || /^#[\w-]*$/.test(h)) return h;
  return null;
}

// Imagenes externas: solo https/http (las del bucket van como kb-adjunto).
export const imagenSegura = (src: string) => (/^https?:\/\//i.test(src.trim()) ? src.trim() : null);

// ---------- En linea ----------

const RE_EN_LINEA =
  /(`+)([\s\S]*?[^`])\1(?!`)|!\[([^\]]*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)|\[((?:[^[\]]|\[[^\]]*\])*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)|\*\*([\s\S]+?)\*\*|__([\s\S]+?)__|\*(?!\s)([^*\n]+?)\*(?!\*)|<(https?:\/\/[^\s>]+)>|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"»])/g;

export function enLinea(texto: string): EnLinea[] {
  const out: EnLinea[] = [];
  const texto_ = (v: string) => {
    if (!v) return;
    const partes = v.split('\n');
    partes.forEach((p, i) => {
      if (i) out.push({ t: 'salto' });
      if (p) {
        const ult = out[out.length - 1];
        if (ult?.t === 'texto') ult.v += p;
        else out.push({ t: 'texto', v: p });
      }
    });
  };
  let ultimo = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(RE_EN_LINEA.source, 'g');
  while ((m = re.exec(texto))) {
    texto_(texto.slice(ultimo, m.index));
    ultimo = m.index + m[0].length;
    if (m[1]) out.push({ t: 'codigo', v: m[2].replace(/^ (.*) $/, '$1') });
    else if (m[4] !== undefined) {
      const alt = m[3] ?? '';
      const src = m[4];
      const adj = /^kb-adjunto:([\w-]{1,64})$/i.exec(src);
      if (adj) out.push({ t: 'adjunto', id: adj[1], nombre: alt.trim() || 'Adjunto' });
      else {
        const ok = imagenSegura(src);
        if (ok) out.push({ t: 'imagen', src: ok, alt });
        else texto_(alt);
      }
    } else if (m[6] !== undefined) {
      const etiqueta = m[5] ?? '';
      const href = m[6];
      const acc = /^acceso:([\w-]{1,64})$/i.exec(href);
      if (acc) out.push({ t: 'acceso', id: acc[1], titulo: etiqueta.replace(/^\s*🔒?\s*(Acceso\s*:\s*)?/i, '').trim() || 'Acceso' });
      else {
        const ok = enlaceSeguro(href);
        if (ok) out.push({ t: 'enlace', href: ok, hijos: enLinea(etiqueta) });
        else out.push(...enLinea(etiqueta));
      }
    } else if (m[7] !== undefined || m[8] !== undefined) out.push({ t: 'negrita', hijos: enLinea(m[7] ?? m[8]) });
    else if (m[9] !== undefined) out.push({ t: 'cursiva', hijos: enLinea(m[9]) });
    else {
      const url = m[10] ?? m[11];
      out.push({ t: 'enlace', href: url, hijos: [{ t: 'texto', v: url }] });
    }
  }
  texto_(texto.slice(ultimo));
  return out;
}

// Texto sin marcas de una lista de nodos en linea.
export function textoDe(xs: EnLinea[]): string {
  return xs
    .map((x) => {
      switch (x.t) {
        case 'texto':
        case 'codigo':
          return x.v;
        case 'negrita':
        case 'cursiva':
        case 'enlace':
          return textoDe(x.hijos);
        case 'imagen':
          return x.alt;
        case 'adjunto':
          return x.nombre;
        case 'acceso':
          return x.titulo;
        case 'salto':
          return ' ';
      }
    })
    .join('');
}

// ---------- Bloques ----------

const RE_VALLA = /^ {0,3}(`{3,}|~{3,})\s*([\w+#.-]*)/;
const RE_TITULO = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RE_REGLA = /^ {0,3}([-*_])(\s*\1){2,}\s*$/;
const RE_VINETA = /^( {0,3})([-*+•])\s+(.*)$/;
const RE_NUMERO = /^( {0,3})(\d{1,4})[.)]\s+(.*)$/;
const RE_CITA = /^ {0,3}>\s?(.*)$/;
const RE_SEPARADOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const esTabla = (l: string, sig: string | undefined) => l.includes('|') && sig !== undefined && RE_SEPARADOR.test(sig) && sig.includes('-');
const celdas = (l: string) => {
  const t = l.trim().replace(/^\|/, '').replace(/\|$/, '');
  return t.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
};

export function slug(t: string) {
  return (
    t
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'seccion'
  );
}

// Una linea que empieza otro bloque (corta un parrafo).
const empiezaBloque = (l: string, sig: string | undefined) =>
  RE_VALLA.test(l) || RE_TITULO.test(l) || RE_REGLA.test(l) || RE_VINETA.test(l) || RE_NUMERO.test(l) || RE_CITA.test(l) || esTabla(l, sig);

// Saca la sangria comun de las lineas de continuacion de un item de lista.
const quitarSangria = (l: string, n: number) => {
  let i = 0;
  while (i < n && (l[i] === ' ' || l[i] === '\t')) i += l[i] === '\t' ? n : 1;
  return l.slice(Math.min(i, l.length));
};

type Ctx = { ids: Map<string, number> };

function leerBloques(lineas: string[], ctx: Ctx): Bloque[] {
  const out: Bloque[] = [];
  let i = 0;
  while (i < lineas.length) {
    const l = lineas[i];
    if (!l.trim()) {
      i++;
      continue;
    }
    // Codigo cercado
    const valla = RE_VALLA.exec(l);
    if (valla) {
      const marca = valla[1];
      const cuerpo: string[] = [];
      i++;
      while (i < lineas.length && !new RegExp(`^ {0,3}${marca[0] === '`' ? '`' : '~'}{${marca.length},}\\s*$`).test(lineas[i])) cuerpo.push(lineas[i++]);
      i++;
      out.push({ t: 'codigo', lenguaje: valla[2] ?? '', texto: cuerpo.join('\n') });
      continue;
    }
    const tit = RE_TITULO.exec(l);
    if (tit) {
      const hijos = enLinea(tit[2]);
      const texto = textoDe(hijos);
      const base = slug(texto);
      const n = (ctx.ids.get(base) ?? 0) + 1;
      ctx.ids.set(base, n);
      out.push({ t: 'titulo', nivel: tit[1].length, id: n > 1 ? `${base}-${n}` : base, hijos, texto });
      i++;
      continue;
    }
    if (RE_REGLA.test(l)) {
      out.push({ t: 'regla' });
      i++;
      continue;
    }
    // Cita o aviso
    if (RE_CITA.test(l)) {
      const dentro: string[] = [];
      while (i < lineas.length && lineas[i].trim() && RE_CITA.test(lineas[i])) dentro.push(RE_CITA.exec(lineas[i++])![1]);
      out.push(cita(leerBloques(dentro, ctx)));
      continue;
    }
    // Tabla
    if (esTabla(l, lineas[i + 1])) {
      const encabezado = celdas(l);
      const alineacion: Alineacion[] = celdas(lineas[i + 1]).map((c) => (c.startsWith(':') && c.endsWith(':') ? 'centro' : c.endsWith(':') ? 'der' : c.startsWith(':') ? 'izq' : null));
      i += 2;
      const filas: EnLinea[][][] = [];
      while (i < lineas.length && lineas[i].trim() && lineas[i].includes('|')) filas.push(celdas(lineas[i++]).map(enLinea));
      out.push({ t: 'tabla', encabezado: encabezado.map(enLinea), alineacion, filas });
      continue;
    }
    // Listas (con vinetas o numeradas = pasos)
    const vin = RE_VINETA.exec(l);
    const num = RE_NUMERO.exec(l);
    if (vin || num) {
      const ordenada = !!num;
      const items: string[][] = [];
      let inicio = num ? Number(num[2]) : 1;
      while (i < lineas.length) {
        const x = lineas[i];
        const mv = RE_VINETA.exec(x);
        const mn = RE_NUMERO.exec(x);
        const mismo = ordenada ? mn : mv;
        if (mismo) {
          const ancho = mismo[1].length + (ordenada ? mismo[2].length + 2 : 2);
          const item = [mismo[3]];
          // Otro item de esta misma lista (y no uno de una lista anidada con mas sangria).
          const hermano = (y: string) => {
            const mm = (ordenada ? RE_NUMERO : RE_VINETA).exec(y);
            return !!mm && mm[1].length < ancho;
          };
          i++;
          // Continuacion: lineas con sangria, o lineas en blanco seguidas de sangria.
          while (i < lineas.length) {
            const y = lineas[i];
            if (!y.trim()) {
              const sig = lineas.slice(i + 1).find((z) => z.trim());
              if (sig !== undefined && /^\s{2,}\S/.test(sig) && !hermano(sig)) {
                item.push('');
                i++;
                continue;
              }
              break;
            }
            if (/^\s{2,}\S/.test(y) && !hermano(y)) {
              item.push(quitarSangria(y, ancho));
              i++;
              continue;
            }
            // Linea pegada sin sangria que no empieza otro bloque: sigue el mismo item (texto partido).
            if (!empiezaBloque(y, lineas[i + 1])) {
              item.push(y);
              i++;
              continue;
            }
            break;
          }
          items.push(item);
          // Un renglon en blanco entre items de la misma lista no la corta.
          if (i < lineas.length && !lineas[i].trim()) {
            const sig = lineas.slice(i).findIndex((z) => z.trim());
            const z = sig >= 0 ? lineas[i + sig] : undefined;
            if (z !== undefined && (ordenada ? RE_NUMERO : RE_VINETA).test(z)) {
              i += sig;
              continue;
            }
          }
          continue;
        }
        break;
      }
      if (!ordenada) out.push({ t: 'lista', items: items.map((it) => leerBloques(it, ctx)) });
      else {
        if (!Number.isFinite(inicio) || inicio < 1) inicio = 1;
        out.push({ t: 'pasos', inicio, items: items.map((it) => paso(it, ctx)) });
      }
      continue;
    }
    // Parrafo
    const par: string[] = [l.trim()];
    i++;
    while (i < lineas.length && lineas[i].trim() && !empiezaBloque(lineas[i], lineas[i + 1])) par.push(lineas[i++].trim());
    out.push({ t: 'parrafo', hijos: enLinea(par.join('\n')) });
  }
  return out;
}

// Un paso: "**Titulo** resto" -> titulo y cuerpo; si el paso tiene mas lineas, la primera es el titulo.
function paso(lineas: string[], ctx: Ctx): { titulo: EnLinea[] | null; cuerpo: Bloque[] } {
  const [primera, ...resto] = lineas;
  const neg = /^\*\*(.+?)\*\*[\s:.\-—–]*(.*)$/.exec(primera ?? '');
  if (neg) {
    const sigue = [neg[2], ...resto].join('\n');
    return { titulo: enLinea(neg[1].replace(/[:.]\s*$/, '')), cuerpo: leerBloques(sigue.split('\n'), ctx) };
  }
  if (resto.some((x) => x.trim())) return { titulo: enLinea(primera), cuerpo: leerBloques(resto, ctx) };
  return { titulo: null, cuerpo: leerBloques(lineas, ctx) };
}

const AVISOS: [RegExp, TipoAviso][] = [
  [/^(ojo|cuidado|atenci[oó]n|advertencia)$/i, 'ojo'],
  [/^(consejo|tip|sugerencia|nota)$/i, 'consejo'],
  [/^importante$/i, 'importante'],
];

// "> **Ojo:** texto" -> aviso; cualquier otra cita queda como cita.
function cita(cuerpo: Bloque[]): Bloque {
  const p = cuerpo[0];
  if (p?.t === 'parrafo' && p.hijos[0]?.t === 'negrita') {
    const marca = textoDe(p.hijos[0].hijos).trim();
    const sinDos = marca.replace(/:$/, '').trim();
    const tipo = AVISOS.find(([re]) => re.test(sinDos))?.[1];
    if (tipo) {
      const hijos = p.hijos.slice(1);
      // Quita los dos puntos que quedaron fuera de la negrita ("**Ojo**: …") y el espacio inicial.
      if (hijos[0]?.t === 'texto') {
        const v = hijos[0].v.replace(/^\s*:?\s*/, '');
        if (v) hijos[0] = { t: 'texto', v };
        else hijos.shift();
      }
      if (hijos[0]?.t === 'salto') hijos.shift();
      return { t: 'aviso', tipo, cuerpo: [...(hijos.length ? [{ t: 'parrafo' as const, hijos }] : []), ...cuerpo.slice(1)] };
    }
  }
  return { t: 'cita', cuerpo };
}

export function bloquesMarkdown(texto: string): Bloque[] {
  const lineas = String(texto ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, '    ')
    .split('\n');
  return leerBloques(lineas, { ids: new Map() });
}

// Titulos para el indice «En este artículo» (niveles 1 a 3).
export function indice(bloques: Bloque[]) {
  return bloques.filter((b): b is Extract<Bloque, { t: 'titulo' }> => b.t === 'titulo' && b.nivel <= 3).map((b) => ({ id: b.id, texto: b.texto, nivel: b.nivel }));
}

// ---------- Texto plano (para respuestas al cliente y resumenes) ----------

// Texto sin marcas para pegar en un correo: los adjuntos quedan como [Adjunto: nombre] y los accesos se quitan (nunca
// salen hacia el cliente).
function planoEnLinea(xs: EnLinea[]): string {
  return xs
    .map((x) => {
      switch (x.t) {
        case 'texto':
        case 'codigo':
          return x.v;
        case 'negrita':
        case 'cursiva':
          return planoEnLinea(x.hijos);
        case 'enlace': {
          const t = planoEnLinea(x.hijos);
          return t && t !== x.href && !x.href.startsWith('/') && !x.href.startsWith('#') ? `${t} (${x.href})` : t || x.href;
        }
        case 'imagen':
          return x.alt ? `[Imagen: ${x.alt}]` : '';
        case 'adjunto':
          return `[Adjunto: ${x.nombre}]`;
        case 'acceso':
          return '';
        case 'salto':
          return '\n';
      }
    })
    .join('')
    .replace(/[ \t]+\n/g, '\n');
}

function planoBloques(bs: Bloque[], sangria = ''): string {
  const partes: string[] = [];
  for (const b of bs) {
    switch (b.t) {
      case 'titulo':
        partes.push(planoEnLinea(b.hijos));
        break;
      case 'parrafo': {
        const t = planoEnLinea(b.hijos).trim();
        if (t) partes.push(t);
        break;
      }
      case 'lista':
        partes.push(b.items.map((it) => `${sangria}- ${planoBloques(it, sangria + '  ').replace(/\n/g, `\n${sangria}  `)}`).join('\n'));
        break;
      case 'pasos':
        partes.push(
          b.items
            .map((it, k) => {
              const cuerpo = planoBloques(it.cuerpo, sangria + '   ');
              const tit = it.titulo ? planoEnLinea(it.titulo) : '';
              const todo = [tit, cuerpo].filter(Boolean).join(tit && cuerpo ? '\n' : '');
              return `${sangria}${b.inicio + k}. ${todo.replace(/\n/g, `\n${sangria}   `)}`;
            })
            .join('\n'),
        );
        break;
      case 'codigo':
        partes.push(b.texto);
        break;
      case 'tabla':
        partes.push([b.encabezado, ...b.filas].map((f) => f.map(planoEnLinea).join(' | ')).join('\n'));
        break;
      case 'aviso': {
        const t = planoBloques(b.cuerpo, sangria);
        partes.push(`${b.tipo === 'ojo' ? 'Ojo' : b.tipo === 'consejo' ? 'Consejo' : 'Importante'}: ${t}`);
        break;
      }
      case 'cita':
        partes.push(planoBloques(b.cuerpo, sangria));
        break;
      case 'regla':
        break;
    }
  }
  return partes.filter((p) => p.trim()).join('\n\n');
}

export const textoPlanoMarkdown = (md: string) => planoBloques(bloquesMarkdown(md)).replace(/\n{3,}/g, '\n\n').trim();
